import {
  newHonorariumClientRowId,
  type U3129FindProjectItem,
  type U3129FindSubprojectItem,
  type U3129HonorariumItem,
  type U3129HonorariumProjectRef,
  type U3129TaskItem,
} from "../U3129Service";
import { u3129FormatTemplateTaskStorageDescription } from "./u3129HonorariumTemplate";
import { u3129HonorariumTaskWorkNatureFieldsFromTaskItem } from "./u3129HonorariumWorkNatureDisplay";

/** Clé stable projet + sous-projet + type de tâche (dédoublonnage lignes honoraires). */
export function u3129HonorariumLineProjectSubprojectTaskKeyFromParts(projectId: string, subprojectId: string, taskType: string): string {
  return `${String(projectId).trim()}|${String(subprojectId).trim()}|${String(taskType).trim()}`;
}

function u3129ResolveHonorariumLineProjectId(row: Pick<U3129HonorariumItem, "projectId" | "project">): string {
  const pid = row.projectId?.trim();
  if (pid) return pid;
  const p = row.project;
  if (p && typeof p === "object" && "id" in p) {
    return String((p as { id?: string }).id ?? "").trim();
  }
  return "";
}

function u3129ResolveHonorariumLineSubprojectId(row: Pick<U3129HonorariumItem, "subprojectId" | "subproject">): string {
  const sid = row.subprojectId?.trim();
  if (sid) return sid;
  const sp = row.subproject;
  if (sp && typeof sp === "object" && "id" in sp) {
    return String((sp as { id?: string }).id ?? "").trim();
  }
  return "";
}

function u3129ResolveHonorariumLineTaskType(row: Pick<U3129HonorariumItem, "taskType" | "task">): string {
  return row.taskType?.trim() || row.task?.id?.trim() || "";
}

export function u3129HonorariumLineProjectSubprojectTaskKey(
  row: Pick<U3129HonorariumItem, "projectId" | "subprojectId" | "taskType" | "project" | "subproject" | "task">,
): string {
  return u3129HonorariumLineProjectSubprojectTaskKeyFromParts(
    u3129ResolveHonorariumLineProjectId(row),
    u3129ResolveHonorariumLineSubprojectId(row),
    u3129ResolveHonorariumLineTaskType(row),
  );
}

/** Retire les candidats dont la clé (projet / sous-projet / tâche) existe déjà dans `existing`. */
export function u3129FilterHonorariumLinesNotDuplicatingExisting(candidates: U3129HonorariumItem[], existing: U3129HonorariumItem[]): U3129HonorariumItem[] {
  const keys = new Set(existing.map(u3129HonorariumLineProjectSubprojectTaskKey));
  return candidates.filter((r) => !keys.has(u3129HonorariumLineProjectSubprojectTaskKey(r)));
}

/** Même règle que le backend `u3100IsAgmmCustomerName` (BL-0679). */
export function u3129IsAgmmCustomerName(nomClient?: string | null): boolean {
  return (nomClient ?? "").toLocaleLowerCase("fr").includes("agmm");
}

/** Libellé bloc client pour un résultat `findProject` (AGMM → `projetName`, sinon nom + code). */
export function u3129ClientDisplayFromFindProject(project: U3129FindProjectItem): string {
  const customerName = project.customerName?.trim() ?? "";
  if (u3129IsAgmmCustomerName(customerName)) {
    const desc = project.projetName?.trim();
    if (desc) {
      return desc;
    }
  }
  if (project.customerName != null || project.customerCode != null) {
    return [project.customerName, project.customerCode != null ? `(${project.customerCode})` : ""].filter(Boolean).join(" ");
  }
  return "";
}

/** Libellé bloc client depuis un projet GET ou find (BL-0679 : clientBlockLabel prioritaire). */
export function u3129ClientBlockLabelFromProject(
  project: U3129FindProjectItem | U3129HonorariumProjectRef | null | undefined,
): string {
  if (!project || typeof project !== "object") {
    return "";
  }
  if ("clientBlockLabel" in project) {
    const block = project.clientBlockLabel?.trim();
    if (block) {
      return block;
    }
  }
  if ("nomClient" in project) {
    const nom = project.nomClient?.trim();
    const desc = "projetName" in project ? project.projetName?.trim() : undefined;
    if (nom && u3129IsAgmmCustomerName(nom) && desc) {
      return desc;
    }
    if (nom) {
      return nom;
    }
  }
  if ("customerName" in project) {
    return u3129ClientDisplayFromFindProject(project as U3129FindProjectItem).trim();
  }
  return "";
}

/** Client affichable pour une ligne honoraire (GET API, find projet ou `clientDisplay` UI). */
export function u3129ClientDisplayFromHonorariumRow(row: U3129HonorariumItem | null | undefined): string {
  if (!row) {
    return "";
  }
  const fromProject = u3129ClientBlockLabelFromProject(
    row.project as U3129FindProjectItem | U3129HonorariumProjectRef | undefined,
  );
  if (fromProject) {
    return fromProject;
  }
  const rowRec = row as unknown as Record<string, unknown>;
  const rawClient = rowRec.clientDisplay;
  if (rawClient != null && String(rawClient).trim() !== "") {
    return String(rawClient).trim();
  }
  return "";
}

/** Libellé projet affiché en grille honoraires (numéro de dossier, repli id). */
export function u3129ProjectDisplayLabel(
  project: U3129FindProjectItem | U3129HonorariumProjectRef | null | undefined,
  projectId?: string | null,
): string {
  if (project && typeof project === "object") {
    const num = "projetNumber" in project ? project.projetNumber?.trim() : undefined;
    if (num) return num;
    const id = "id" in project ? String(project.id ?? "").trim() : "";
    if (id) return id;
  }
  return projectId?.trim() ?? "";
}

function projectRequiresSubproject(project: U3129FindProjectItem): boolean {
  const v = project.includeSubProjet;
  return v === true || (typeof v === "string" && v === "true");
}

/**
 * Construit N lignes honoraires (une par tâche) avec le même projet / sous-projet et heures à zéro.
 */
export function buildHonorariumLinesFromProjectAndTasks(
  project: U3129FindProjectItem,
  subproject: U3129FindSubprojectItem | null,
  tasks: U3129TaskItem[],
  options: { admIncludeSubProjectEnabled: boolean },
): U3129HonorariumItem[] {
  const { admIncludeSubProjectEnabled } = options;
  const clientDisplay = u3129ClientDisplayFromFindProject(project);
  const projectDisplay = u3129ProjectDisplayLabel(project, project.id);
  const needSub = admIncludeSubProjectEnabled && projectRequiresSubproject(project);

  return tasks.map((task) => {
    const row: U3129HonorariumItem & { clientDisplay?: string; projectDisplay?: string } = {
      clientRowId: newHonorariumClientRowId(),
      projectId: project.id,
      project,
      clientDisplay,
      projectDisplay,
      taskType: task.id,
      task: {
        id: task.id,
        description: u3129FormatTemplateTaskStorageDescription(task),
        ...u3129HonorariumTaskWorkNatureFieldsFromTaskItem(task),
      },
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };
    if (needSub && subproject) {
      row.subprojectId = subproject.id;
      row.subproject = subproject;
    } else {
      row.subprojectId = "";
      row.subproject = undefined;
    }
    return row;
  });
}
